import * as React from 'react';

import { FilterChip } from '@/design';
import type {
  IAdminTeamRow,
  ITeamsQuery,
  PeopleTeamRouting,
  PeopleTeamRoutingSource,
  TeamCard,
  TeamFacetField
} from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { RadioFilter } from '../../components/admin-page';
import {
  LEAD_SHARED,
  optionLabel,
  ROSTER_STATES,
  TEAM_ROUTING_SOURCES,
  TEAM_ROUTINGS,
  withUnknownOption
} from '../../components/record-filters';
import { OUTCOME_WORDS, SOURCE_WORDS } from '../person-hub/team';
import { ParamChip } from '../sec-adv/firm-filters';
import {
  accountColumn,
  createdColumn,
  latestResponseColumn,
  recordNameColumn,
  recordQueryOf,
  RosterSlots,
  type IFilterContext
} from './record-list';
import { RecordWorkArea, RecordWorkPage, type IRecordWorkSpec } from './record-work-page';

/**
 * Teams (SR-414, record search §3.2; the plan's D14) — one row per team, `GET /admin/teams` under
 * `responses.browse`; and a team's record page beside the list at its own address,
 * `/team/teams/:participantId` (the owner's Q2). The record page is People's — one page drawn under
 * three lists — and moves itself here once its hub says it is a team (D6).
 *
 * A row is the team, its lead (slot 1) and its other members as its roster resolves them, its
 * routing as survey-api derived it, the account acting for it, and its newest response. The
 * row carries no archive member: the record page's Team section says whether it is archived.
 *
 * The page is a segmented work page (SR-514, `record-work-page.tsx`): view cards with counts —
 * All teams, Wires, Independent, RIA, Not routed yet, Archived — and no signals. A card is always sent,
 * `all` where the URL names none: All is the live teams, the archived ones are under Archived
 * alone, and a read with no `card` would list both. Where a team is routed (`unknown` — nowhere
 * yet, the manual queue) and what that rests on are the Routing column's funnel; a lead CRD
 * another team holds is the toolbar's radio pair, since survey-api binds `teamLeadShared` as a
 * `bool` and a spelling that is not `true` is its binder's 400, said as sent.
 */
export function AdminTeamsArea() {
  return <RecordWorkArea spec={TEAMS} />;
}

export function AdminTeamsPage() {
  return <RecordWorkPage spec={TEAMS} />;
}

/** The Teams query, from the URL — the shared filters, the three team ones and the card, as
 *  spelled. */
function teamsQueryOf(params: URLSearchParams, page: number, pageSize: number): ITeamsQuery {
  return {
    ...recordQueryOf(params, page),
    pageSize,
    // All, the URL's clean default, is sent by name: survey-api reads no card as every team.
    card: (params.get('card') ?? 'all') as TeamCard,
    teamRouting: (params.get('teamRouting') ?? undefined) as PeopleTeamRouting | undefined,
    teamRoutingSource: (params.get('teamRoutingSource') ?? undefined) as
      | PeopleTeamRoutingSource
      | undefined,
    // `true` narrows; any other spelling goes as it is, for survey-api's binder to refuse.
    teamLeadShared: (params.get('teamLeadShared') ?? undefined) as unknown as boolean | undefined
  };
}

function LeadShared({ params, choose }: IFilterContext) {
  const leadShared = params.get('teamLeadShared');
  return (
    <FilterChip label="Lead CRD" summary={optionLabel(LEAD_SHARED, leadShared)}>
      <RadioFilter
        label="Lead CRD"
        name="teams-lead-shared"
        options={withUnknownOption(LEAD_SHARED, leadShared)}
        value={leadShared}
        onChoose={choose('teamLeadShared')}
      />
    </FilterChip>
  );
}

/** A team's routing in words — its outcome and what it rests on; "Not routed yet", the record
 *  page's words, while survey-api routes it nowhere. */
function Routing({ team }: { team: IAdminTeamRow }) {
  const { routing } = team;
  if (routing === null || routing.outcome === null)
    return <span className="text-muted-foreground">{SOURCE_WORDS.none}</span>;
  return (
    <>
      {OUTCOME_WORDS[routing.outcome] ?? routing.outcome}
      <p className="mt-0.5 text-xs text-muted-foreground">
        {SOURCE_WORDS[routing.source] ?? routing.source}
      </p>
    </>
  );
}

const TEAMS: IRecordWorkSpec<IAdminTeamRow, ITeamsQuery> = {
  area: 'teams',
  counted: ['team', 'teams'],
  placeholder: 'Team, member, address or number',
  about:
    'The teams we hold: All teams is the live ones, and an archived team is under Archived ' +
    'alone. Open a team for its record beside the list.',
  filters: ['teamRouting', 'teamRoutingSource', 'teamLeadShared'],
  queryOf: teamsQueryOf,
  list: (query, signal) => participantsApi.listTeams(query, signal),
  facets: (field, query, prefix, signal) =>
    participantsApi.teamFacets(field as TeamFacetField, query, prefix, signal),
  sorts: {
    name: { key: 'name', first: 'asc' },
    'latest-response': { key: 'last_change', first: 'desc' },
    created: { key: 'created', first: 'desc' }
  },
  funnels: [
    {
      column: 'routing',
      groups: [
        { param: 'teamRouting', label: 'Routing', known: TEAM_ROUTINGS },
        { param: 'teamRoutingSource', label: 'Routing decided by', known: TEAM_ROUTING_SOURCES }
      ]
    },
    { column: 'members', groups: [{ param: 'roster', label: 'Roster', known: ROSTER_STATES }] },
    { column: 'latest-response', groups: [{ param: 'cycle', label: 'Cycle', known: 'cycles' }] }
  ],
  quick: (context) => <LeadShared {...context} />,
  quickChips: ({ params, choose }, open) => {
    const leadShared = params.get('teamLeadShared');
    if (leadShared === null) return [];
    return [
      {
        key: 'teamLeadShared',
        chip: (
          <ParamChip
            words={`Lead CRD: ${optionLabel(LEAD_SHARED, leadShared) ?? leadShared}`}
            body={null}
            onPress={() => open('Lead CRD')}
            onRemove={() => choose('teamLeadShared')(null)}
          />
        )
      }
    ];
  },
  cards: {
    list: [
      {
        key: 'all',
        label: 'All teams',
        hint: 'Every live team',
        about: 'every live team we hold.'
      },
      {
        key: 'wires',
        label: 'Wires',
        hint: 'Live teams routed to Wires',
        about: 'the live teams routed to the Wires survey.'
      },
      {
        key: 'independent',
        label: 'Independent',
        hint: 'Live teams routed to Independent',
        about: 'the live teams routed to the Independent survey.'
      },
      {
        key: 'ria',
        label: 'RIA',
        hint: 'Live teams routed to the RIA firm survey',
        about: 'the live teams routed to the RIA firm survey — their firm answers it, not the team.'
      },
      {
        key: 'unrouted',
        label: 'Not routed yet',
        hint: 'Live teams routed nowhere yet',
        about: 'the live teams nothing has routed yet — the manual queue.'
      },
      {
        key: 'archived',
        label: 'Archived',
        hint: 'Archived teams',
        about: 'the teams archived.'
      }
    ],
    counts: (query, signal) => participantsApi.teamCounts(query, signal)
  },
  columns: (search, q) => [
    recordNameColumn<IAdminTeamRow>('teams', 'Team', search, q),
    {
      id: 'lead',
      header: 'Lead',
      cell: (team) => (
        <RosterSlots label="Lead" members={team.members.filter((slot) => slot.slot === 1)} />
      )
    },
    {
      id: 'members',
      header: 'Members',
      cell: (team) => (
        <RosterSlots label="Members" members={team.members.filter((slot) => slot.slot !== 1)} />
      )
    },
    { id: 'routing', header: 'Routing', cell: (team) => <Routing team={team} /> },
    accountColumn(),
    latestResponseColumn(),
    createdColumn()
  ],
  secondary: (team) => {
    const lead = team.members.find((slot) => slot.slot === 1);
    return lead?.member?.displayName ?? lead?.fullName ?? 'No lead named';
  }
};
